import React from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Chip, 
  Alert,
  Button
} from '@mui/material';
import BookIcon from '@mui/icons-material/Book';
import { ETTCalculations } from '../utils/ettUtils';

interface ETTCalculationsSectionProps {
  ettCalculations: ETTCalculations;
  measurements: {
    trachea: { transverseDiameter: number };
    cricoid: { apDiameter: number };
  };
  showETTCalculations: boolean;
  onToggleETTCalculations: () => void;
  onNavigateToReferences?: () => void;
  // Input values for display
  ageValue?: string;
  heightInput?: string;
  weightInput?: string;
  heightUnit?: 'cm' | 'in';
  weightUnit?: 'kg' | 'lb';
  results?: any; // For age category and BMI
}

export const ETTCalculationsSection: React.FC<ETTCalculationsSectionProps> = React.memo(({
  ettCalculations,
  measurements,
  showETTCalculations,
  onToggleETTCalculations,
  onNavigateToReferences,
  ageValue,
  heightInput,
  weightInput,
  heightUnit,
  weightUnit,
  results,
}) => {
  const getFormulaText = (formula: string) => {
    switch (formula) {
      case 'Cole': return 'age (years) ÷ 4 + 4 mm';
      case 'Cole (Adjusted)': return 'age (years) ÷ 4 + 4 mm';
      case 'Motoyama': return 'age (years) ÷ 4 + 3 mm';
      case 'Motoyama (Adjusted)': return 'age (years) ÷ 4 + 3 mm';
      case 'Khine': return 'age (years) ÷ 4 + 3 mm';
      case 'Khine (Adjusted)': return 'age (years) ÷ 4 + 3 mm';
      case 'Duracher': return 'age (years) ÷ 4 + 3.5 mm';
      case 'Duracher (Adjusted)': return 'age (years) ÷ 4 + 3.5 mm';
      case 'Weight-based': return 'weight (kg) ÷ 10 + 3.5 mm';
      case 'Weight-based (Adjusted)': return 'weight (kg) ÷ 10 + 3.5 mm';
      default: return '';
    }
  };

  const renderCalculationRow = (formula: string, type: string, size: number, notes: string, reference?: string) => (
    <Box key={formula} sx={{ 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center',
      py: 1.5,
      px: 2,
      borderBottom: '1px solid #e0e0e0',
      '&:last-child': { borderBottom: 'none' }
    }}>
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2' }}>
          {formula}
        </Typography>
        <Typography variant="caption" sx={{ color: '#888', display: 'block', fontStyle: 'italic', mb: 0.5 }}>
          {getFormulaText(formula)}
        </Typography>
        <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
          {type} • {notes}
        </Typography>
      </Box>
      <Chip
        label={`${size.toFixed(1)} mm`}
        color="primary"
        size="small"
        sx={{
          fontWeight: 600,
          minWidth: '60px'
        }}
      />
    </Box>
  );

  return (
    <Box>
      {/* Input Values Display */}
      {(ageValue || heightInput || weightInput) && (
        <Box sx={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 1, 
          mb: 2, 
          p: 0,
          borderRadius: 2,
          justifyContent: 'center'
        }}>
          {ageValue && (
            <Chip 
              label={`Age: ${ageValue}`} 
              color="primary" 
              variant="outlined"
              sx={{ fontWeight: 500 }}
            />
          )}
          {results?.ageCategory && (
            <Chip 
              label={`Category: ${results.ageCategory}`} 
              color="secondary" 
              variant="outlined"
              sx={{ fontWeight: 500 }}
            />
          )}
          {heightInput && heightUnit && (
            <Chip 
              label={`Height: ${heightInput} ${heightUnit}`} 
              variant="outlined"
              sx={{ fontWeight: 500 }}
            />
          )}
          {weightInput && weightUnit && (
            <Chip 
              label={`Weight: ${weightInput} ${weightUnit}`} 
              variant="outlined"
              sx={{ fontWeight: 500 }}
            />
          )}
          {results?.ettCalculations?.bmi && (
            <Chip 
              label={`BMI: ${results.ettCalculations.bmi.toFixed(1)} (${results.ettCalculations.bmiCategory})`} 
              color={results.ettCalculations.bmiCategory === 'Obese' ? 'warning' : 'default'}
              variant="outlined"
              sx={{ fontWeight: 500 }}
            />
          )}
        </Box>
      )}

      {/* Airway Measurements */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#1976d2', textAlign: 'left' }}>
            Airway Measurements
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ 
              flex: 1, 
              minWidth: '140px',
              p: 1.5,
              backgroundColor: '#f0f7ff',
              borderRadius: 1,
              textAlign: 'center'
            }}>
              <Typography variant="caption" sx={{ display: 'block', color: '#666' }}>
                Trachea Transverse
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2' }}>
                {measurements.trachea.transverseDiameter} mm
              </Typography>
            </Box>
            <Box sx={{ 
              flex: 1, 
              minWidth: '140px',
              p: 1.5,
              backgroundColor: '#f0f7ff',
              borderRadius: 1,
              textAlign: 'center'
            }}>
              <Typography variant="caption" sx={{ display: 'block', color: '#666' }}>
                Cricoid AP
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2' }}>
                {measurements.cricoid.apDiameter} mm
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* ETT Calculations */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ p: 2, pb: 0 }}>
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: '#1976d2', textAlign: 'left' }}>
              ETT Size Calculations
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
              Internal diameter recommendations based on validated formulas
            </Typography>
          </Box>

          <Box>
            {renderCalculationRow(
              'Cole', 
              'Uncuffed', 
              ettCalculations.idealCalculations.cole, 
              'Standard formula for uncuffed ETT',
              '#reference-1'
            )}
            {ettCalculations.idealCalculations.motoyama !== null && renderCalculationRow(
              'Motoyama', 
              'Cuffed', 
              ettCalculations.idealCalculations.motoyama, 
              'For children 2 years or older',
              'https://scholar.google.com/scholar_lookup?title=Endotracheal+intubation&author=EK+Motoyama'
            )}
            {ettCalculations.idealCalculations.khine !== null && renderCalculationRow(
              'Khine', 
              'Cuffed', 
              ettCalculations.idealCalculations.khine, 
              'For children 1 year or older',
              'https://pubmed.ncbi.nlm.nih.gov/9066329/'
            )}
            {ettCalculations.idealCalculations.duracher !== null && renderCalculationRow(
              'Duracher', 
              'Cuffed', 
              ettCalculations.idealCalculations.duracher, 
              'For children 1 year or older',
              'https://pubmed.ncbi.nlm.nih.gov/18184241/'
            )}
            {ettCalculations.idealCalculations.weightBased !== null && renderCalculationRow(
              'Weight-based', 
              'Both', 
              ettCalculations.idealCalculations.weightBased, 
              'Weight/10 + 3.5'
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Obesity Adjusted Calculations */}
      {ettCalculations.bmiCategory === 'Obese' && (
        <Card sx={{ mb: 2 }}>
          <CardContent sx={{ p: 0 }}>
            <Alert severity="info" sx={{ m: 2, mb: 1 }}>
              Obesity-adjusted calculations for BMI ≥95th percentile
            </Alert>
            <Box>
              {renderCalculationRow(
                'Cole (Adjusted)', 
                'Uncuffed', 
                ettCalculations.adjustedCalculations.cole, 
                'Adjusted for obesity'
              )}
              {ettCalculations.adjustedCalculations.motoyama !== null && renderCalculationRow(
                'Motoyama (Adjusted)', 
                'Cuffed', 
                ettCalculations.adjustedCalculations.motoyama, 
                'Adjusted for obesity'
              )}
              {ettCalculations.adjustedCalculations.khine !== null && renderCalculationRow(
                'Khine (Adjusted)', 
                'Cuffed', 
                ettCalculations.adjustedCalculations.khine, 
                'Adjusted for obesity'
              )}
              {ettCalculations.adjustedCalculations.duracher !== null && renderCalculationRow(
                'Duracher (Adjusted)', 
                'Cuffed', 
                ettCalculations.adjustedCalculations.duracher, 
                'Adjusted for obesity'
              )}
              {ettCalculations.adjustedCalculations.weightBased !== null && renderCalculationRow(
                'Weight-based (Adjusted)', 
                'Both', 
                ettCalculations.adjustedCalculations.weightBased, 
                'Adjusted for obesity'
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* References Button */}
      {onNavigateToReferences && (
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Button
            variant="outlined"
            onClick={onNavigateToReferences}
            startIcon={<BookIcon />}
            sx={{
              borderColor: '#1976d2',
              color: '#1976d2',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.04)',
                borderColor: '#1976d2'
              }
            }}
          >
            View Full References
          </Button>
        </Box>
      )}
    </Box>
  );
});